/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC} from 'react'
import {useDispatch} from 'react-redux'
import {setCurrentAccount} from '../../../../slices/accountSlice'
import {useAccount} from '../../../../hooks/useAccount'
import {IAccount} from '../../../../types'
import clsx from 'clsx'
import {KTSVG} from '../../../helpers'
import {useIntl} from 'react-intl'

const hasName = (account: IAccount | undefined) => Boolean(account && account.name && account.name.trim() !== '')

const AccountSelector: FC = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const {accounts, currentAccountId, isAgent} = useAccount()

  const visibleAccounts = [...accounts].sort(
    (a, b) => Number(b.role === 'OWNER') - Number(a.role === 'OWNER') || Number(hasName(a)) - Number(hasName(b))
  )

  const currentAccount = visibleAccounts.find((a) => a.id === currentAccountId) ?? visibleAccounts[0]

  const accountLabel = (account: typeof accounts[number] | undefined) => {
    if (account?.role === 'OWNER') {
      return intl.formatMessage({id: 'ASIDE.MENU.TITLE.MY_ACCOUNT'})
    }

    if (hasName(account)) {
      return account.name
    }

    if (account?.id) {
      return `${account.id.slice(0, 6)}...`
    }

    return intl.formatMessage({id: 'ASIDE.MENU.TITLE.MY_ACCOUNT'})
  }

  const handleSelect = (accountId: string) => {
    if (accountId === currentAccountId) return
    dispatch(setCurrentAccount(accountId))
  }

  if (visibleAccounts.length <= 1) {
    if (!currentAccount) {
      return null
    }

    return (
      <div className='d-flex align-items-center'>
        <div className={clsx('d-flex align-items-center', 'ms-1 ms-lg-3')}>
          <span className='btn btn-active-light-primary px-4 py-2 d-flex align-items-center'>
            <span className='d-flex align-items-center justify-content-center me-2 lh-1' data-kt-element='icon'>
              <KTSVG path='/media/icons/duotune/communication/com006.svg' className='svg-icon-1' />
            </span>
            <span className='text-nowrap lh-1'>{accountLabel(currentAccount)}</span>
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className='d-flex align-items-center'>
      <div className={clsx('d-flex align-items-center', '')}>
        <a
          className='btn btn-active-light-primary  px-4 py-2 d-flex align-items-center'
          data-kt-menu-trigger='click'
          data-kt-menu-attach='parent'
          data-kt-menu-placement='bottom-start'
        >
          <span className='d-flex align-items-center justify-content-center me-2 lh-1' data-kt-element='icon'>
            <KTSVG path='/media/icons/duotune/communication/com006.svg' className='svg-icon-1' />
          </span>

          <span className='text-nowrap lh-1'>
            {accountLabel(currentAccount)}
            {isAgent && currentAccount.internal_numbers?.[0] && <span> - {currentAccount.internal_numbers[0]}</span>}
          </span>
        </a>

        <div
          className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-title-gray-700 menu-icon-muted menu-active-bg menu-state-primary fw-semibold py-4 fs-base w-auto text-nowrap mt-4'
          data-kt-menu='true'
        >
          {visibleAccounts.map((account) => (
            <div
              key={account.id}
              className={clsx('menu-item px-3 my-0', account.id === currentAccountId && 'menu-item-active')}
            >
              <a className='menu-link px-3 py-2' onClick={() => handleSelect(account.id)}>
                <span className='menu-icon' data-kt-element='icon'>
                  <KTSVG path='/media/icons/duotune/communication/com006.svg' className='svg-icon-2' />
                </span>

                <span className='menu-title text-nowrap'>{accountLabel(account)}</span>
                {account.id === currentAccountId && (
                  <span className='menu-badge'>
                    <i className='fas fa-check text-primary fs-9'></i>
                  </span>
                )}
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export {AccountSelector}
